import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAccess } from '@/access/useAccess';
import { catalogApi } from '@/api/endpoints/catalog';
import type { SearchMode, SearchZones, VideoListResponse, VideoReference } from '@/api/types';
import { appConfig } from '@/config/app';
import { toApiParams, type CatalogFilters } from './filterModel';
import { exactCount, noteSearchEngine, searchModeParam } from './searchPhases';

export const videosQueryKey = ['videos'] as const;

export interface VideosResult {
  videos: VideoReference[];
  ids: number[];
  total: number;
  /** Реальное число совпадений до капа (пока только mock, см. VideoListMeta). */
  totalUncapped: number | null;
  subscriptionRequired: boolean;
  freeTierCap: number | null;
  showMoreThreshold: number;
  searchEventId: number | null;
  effectiveQuery: string;
  /** Режим запроса показанной выдачи: аналитика поиска считает только отправленные запросы. */
  searchMode: SearchMode;
  /** Зоны поиска v3; null — отвечает старый конвейер, выдача рисуется одним списком. */
  zones: SearchZones | null;
  /** Сколько из загруженных видео — точные совпадения; null без зон. */
  exactLoaded: number | null;
  interpretedAs: string | null;
  categoryHint: { id: number; name: string } | null;
  didYouMean: string | null;
  isLoading: boolean;
  isFetching: boolean;
  /** Идёт смена выдачи (новый запрос или фильтр): прежние карточки остаются приглушёнными. */
  isRefreshing: boolean;
  /** Меняется при каждой подмене выдачи — ключ для анимации появления новых карточек. */
  swapKey: number;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: unknown;
  fetchNextPage: () => void;
  refetch: () => void;
}

/** Выдача каталога: 24 на страницу, кап free-tier, id поиска в заголовке страниц 2+ (CAT-7, CAT-12, CAT-15). */
export function useVideos(
  filters: CatalogFilters,
  query: string,
  mode: SearchMode = 'submitted',
): VideosResult {
  const { hasFullAccess } = useAccess();
  // Короче трёх символов поиском не считается — конвейер поиска не запускается (CAT-1).
  const effectiveQuery = query.trim().length >= appConfig.searchMinLength ? query.trim() : '';
  // На бэкенд уходит только «набор на лету»; «отправлен» — его поведение по умолчанию (ADR-009, D6).
  const sentMode = searchModeParam(mode, effectiveQuery);
  const contentParams = useMemo(
    () => toApiParams(filters, effectiveQuery),
    [filters, effectiveQuery],
  );
  const baseParams = useMemo(
    () => (sentMode ? { ...contentParams, search_mode: sentMode } : contentParams),
    [contentParams, sentMode],
  );
  const searchEventIdRef = useRef<number | null>(null);

  const result = useInfiniteQuery({
    // Признак полного доступа входит в ключ: после оплаты кеш free-tier не должен остаться (CAT-12).
    queryKey: [...videosQueryKey, baseParams, hasFullAccess],
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }): Promise<VideoListResponse> => {
      const response = await catalogApi.list(
        { ...baseParams, page: pageParam, per_page: appConfig.catalogPageSize },
        pageParam > 1 ? searchEventIdRef.current : null,
        signal,
      );
      if (pageParam === 1) searchEventIdRef.current = response.meta.search_event_id ?? null;
      noteSearchEngine(response.meta);
      return response;
    },
    getNextPageParam: (last) =>
      last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined,
    placeholderData: keepPreviousData,
  });

  // Смена ключа отдаёт прежние страницы как placeholder. Чтобы выдача не мигала на
  // быстрых ответах, новые данные подменяют прежние не раньше catalogRefreshMinMs,
  // а всё это время интерфейс показывает состояние обновления.
  const [held, setHeld] = useState(result.data);
  const [heldMode, setHeldMode] = useState<SearchMode>(mode);
  const [refreshing, setRefreshing] = useState(false);
  // Фаза «набор на лету» ответила пусто: это ещё не результат, ждём полную фазу.
  const [awaitingFinal, setAwaitingFinal] = useState(false);
  const [swapKey, setSwapKey] = useState(0);
  const releaseAt = useRef(0);
  const refreshingRef = useRef(false);
  const shownContent = useRef(contentParams);
  const { data, isPlaceholderData } = result;

  useEffect(() => {
    // Вторая фаза того же запроса («набор» → «отправлен»): содержание то же, меняется только режим.
    // Выдача подменяется молча — без приглушения и без анимации появления, иначе каждый запрос мигал бы дважды.
    const sameContent = shownContent.current === contentParams;
    if (isPlaceholderData) {
      if (sameContent) return;
      if (!refreshingRef.current) {
        refreshingRef.current = true;
        releaseAt.current = Date.now() + appConfig.catalogRefreshMinMs;
        setRefreshing(true);
      }
      return;
    }
    // Пустой ответ фазы «набор на лету» — не результат: на ней бэкенд не зовёт ни перевод, ни реранкер,
    // и вставленный русский текст даёт ноль. Показывать «ничего не найдено» рано — через полсекунды придёт
    // полная фаза; до неё интерфейс остаётся в состоянии поиска с прежней выдачей.
    if (sentMode === 'typing' && data !== undefined && (data.pages[0]?.data.length ?? 0) === 0) {
      setAwaitingFinal(true);
      return;
    }
    const wasRefreshing = refreshingRef.current;
    const wait = wasRefreshing ? Math.max(0, releaseAt.current - Date.now()) : 0;
    const timer = setTimeout(() => {
      refreshingRef.current = false;
      shownContent.current = contentParams;
      setHeld(data);
      setHeldMode(mode);
      setRefreshing(false);
      setAwaitingFinal(false);
      if (wasRefreshing) setSwapKey((k) => k + 1);
    }, wait);
    return () => clearTimeout(timer);
  }, [data, isPlaceholderData, contentParams, mode, sentMode]);

  const first = held?.pages[0];
  const videos = useMemo(() => held?.pages.flatMap((p) => p.data) ?? [], [held]);
  const ids = useMemo(() => videos.map((v) => v.id), [videos]);

  return {
    videos,
    ids,
    total: first?.meta.total ?? 0,
    totalUncapped: first?.meta.total_uncapped ?? null,
    subscriptionRequired: !!first?.subscription_required,
    freeTierCap: first?.free_tier_cap ?? null,
    showMoreThreshold: first?.show_more_threshold ?? 5,
    searchEventId: first?.meta.search_event_id ?? null,
    effectiveQuery,
    searchMode: heldMode,
    zones: first?.meta.zones ?? null,
    exactLoaded: exactCount(first?.meta, videos.length),
    interpretedAs: first?.meta.interpreted_as ?? null,
    categoryHint: first?.meta.category_hint ?? null,
    didYouMean: first?.meta.did_you_mean ?? null,
    isLoading: result.isLoading || (held === undefined && (result.isFetching || awaitingFinal)),
    isFetching: result.isFetching || refreshing || awaitingFinal,
    isRefreshing: refreshing || awaitingFinal,
    swapKey,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: !!result.hasNextPage,
    error: result.error,
    fetchNextPage: () => {
      if (result.hasNextPage && !result.isFetchingNextPage) void result.fetchNextPage();
    },
    refetch: () => void result.refetch(),
  };
}
